import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { VoiceSmartNote } from "@/components/VoiceSmartNote";
import { Loader2, Phone, MessageCircle, Mail, PenLine, AlertTriangle } from "lucide-react";
import { useLeadSettings, wordCount, toLocalInput, statusStyle } from "@/lib/leadsV2";

const CH_ICON = { call: Phone, whatsapp: MessageCircle, email: Mail, none: PenLine };
const CH_LABEL = { call: "Call", whatsapp: "WhatsApp", email: "Email", none: "Update" };

function quickDates() {
  const now = new Date();
  const inHours = (h) => new Date(now.getTime() + h * 3600_000);
  const at = (days, hour) => { const d = new Date(now); d.setDate(d.getDate() + days); d.setHours(hour, 0, 0, 0); return d; };
  return [
    { label: "+2 hours", d: inHours(2) },
    { label: "Tomorrow 10 AM", d: at(1, 10) },
    { label: "Tomorrow 5 PM", d: at(1, 17) },
    { label: "In 3 days", d: at(3, 11) },
    { label: "Next week", d: at(7, 11) },
  ];
}

const ScorePicker = ({ value, onChange }) => (
  <div className="grid grid-cols-10 gap-1" data-testid="outcome-score-picker">
    {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => {
      const on = value === n;
      const tone = n >= 8 ? "border-emerald-600 bg-emerald-600" : n >= 5 ? "border-amber-500 bg-amber-500" : "border-rose-600 bg-rose-600";
      return (
        <button
          type="button" key={n} onClick={() => onChange(n)} data-testid={`outcome-score-${n}`}
          className={`h-10 rounded-md border text-sm font-bold transition-colors ${on
            ? `${tone} text-white`
            : "border-border bg-card hover:bg-accent"}`}
        >{n}</button>
      );
    })}
  </div>
);

/** Mandatory update form. channel: call | whatsapp | email | none */
export const OutcomeSheet = ({ open, lead, channel = "none", onDone, onOpenChange, isAdmin = false }) => {
  const { settings, statusMap } = useLeadSettings();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const mandatory = channel !== "none";

  const leadId = lead?.id;
  useEffect(() => {
    if (open && lead) {
      setForm({ status: lead.status, score: lead.score ?? null, note: "", next: "", outcome: "", lost_reason: "", duration: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, leadId]);

  const kind = statusMap[form.status]?.kind || "open";
  const outcomes = useMemo(
    () => (settings?.outcomes || []).filter((o) => (o.channels || []).includes(channel)),
    [settings, channel],
  );
  const words = wordCount(form.note);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const problems = [];
  if (!form.status) problems.push("status");
  if (!form.score) problems.push("score 1-10");
  if (words < 5) problems.push("notes (5+ words)");
  if (mandatory && !form.outcome) problems.push("outcome");
  if (kind === "open") {
    if (!form.next) problems.push("next follow-up");
    else if (new Date(form.next).getTime() <= Date.now()) problems.push("a FUTURE follow-up");
  }
  if (kind === "lost" && !form.lost_reason) problems.push("lost reason");

  const submit = async () => {
    if (problems.length) { toast.error(`Please add: ${problems.join(", ")}`); return; }
    setSaving(true);
    try {
      const body = { status: form.status, score: form.score, note: form.note.trim(), channel };
      if (kind === "open") body.next_follow_up_at = new Date(form.next).toISOString();
      if (form.outcome) body.outcome = form.outcome;
      if (kind === "lost") body.lost_reason = form.lost_reason;
      if (channel === "call" && form.duration) body.duration_seconds = Math.round(parseFloat(form.duration) * 60);
      const { data } = await api.post(`/v2/leads/${lead.id}/updates`, body);
      toast.success("Update saved");
      onDone?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  if (!lead) return null;
  const Icon = CH_ICON[channel] || PenLine;
  const NoteInput = isAdmin ? Textarea : VoiceSmartNote;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && mandatory) { toast.warning("Save what happened first — this update is mandatory."); return; } onOpenChange?.(v); }}>
      <DialogContent
        className={`max-h-[92vh] max-w-lg overflow-y-auto ${mandatory ? "[&>button.absolute]:hidden" : ""}`}
        onInteractOutside={(e) => mandatory && e.preventDefault()}
        onEscapeKeyDown={(e) => mandatory && e.preventDefault()}
        data-testid="outcome-sheet"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display">
            <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-primary"><Icon className="h-4 w-4" /></span>
            {mandatory ? `${CH_LABEL[channel]} outcome` : "Add update"} — {lead.name}
          </DialogTitle>
          <DialogDescription>
            {mandatory ? "Required after every contact. Status, score, notes and next follow-up are all mandatory." : "Every update needs a status, score, 5+ word note and next follow-up."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          {mandatory && (
            <div className="space-y-1.5">
              <Label>What happened? *</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="outcome-options">
                {outcomes.map((o) => (
                  <button key={o.key} type="button" onClick={() => set("outcome", o.key)} data-testid={`outcome-opt-${o.key}`}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${form.outcome === o.key ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:bg-accent"}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>Status * <span className="mono text-[10px] text-muted-foreground">(shared with the other agent)</span></Label>
            <div className="flex flex-wrap gap-1.5" data-testid="outcome-status-options">
              {(settings?.statuses || []).map((s) => (
                <button key={s.key} type="button" onClick={() => set("status", s.key)} data-testid={`outcome-status-${s.key}`}
                  className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${form.status === s.key ? "ring-2 ring-offset-1 ring-offset-background" : "opacity-80 hover:opacity-100"}`}
                  style={{ ...statusStyle(s.color), ...(form.status === s.key ? { "--tw-ring-color": s.color } : {}) }}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Lead score * <span className="mono text-[10px] text-muted-foreground">1 = cold · 10 = ready to buy</span></Label>
            <ScorePicker value={form.score} onChange={(n) => set("score", n)} />
          </div>

          {kind === "lost" && (
            <div className="space-y-1.5">
              <Label>Lost reason *</Label>
              <div className="flex flex-wrap gap-1.5" data-testid="outcome-lost-reasons">
                {(settings?.lost_reasons || []).map((r) => (
                  <button key={r} type="button" onClick={() => set("lost_reason", r)} data-testid={`outcome-lost-${r}`}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold ${form.lost_reason === r ? "border-rose-600 bg-rose-600 text-white" : "border-border bg-card hover:bg-accent"}`}>
                    {r}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label>Notes *</Label>
              <span data-testid="outcome-word-count" className={`mono text-[10px] uppercase tracking-widest ${words >= 5 ? "text-emerald-600" : "text-rose-600"}`}>
                {words}/5 words
              </span>
            </div>
            <NoteInput
              value={form.note || ""}
              onChange={(e) => set("note", typeof e === "string" ? e : e.target.value)}
              rows={3}
              {...(isAdmin ? { "data-testid": "outcome-note-input" } : { context: "note", testId: "outcome-note" })}
              placeholder="What did the customer say? What's the next step?"
            />
          </div>

          {kind === "open" ? (
            <div className="space-y-1.5">
              <Label>Your next follow-up * <span className="mono text-[10px] text-muted-foreground">(only yours — the other agent keeps their own)</span></Label>
              <div className="flex flex-wrap gap-1.5">
                {quickDates().map((q) => (
                  <button key={q.label} type="button" onClick={() => set("next", toLocalInput(q.d))} data-testid={`outcome-quick-${q.label.replace(/\s+/g, "-").toLowerCase()}`}
                    className="rounded-md border border-border bg-card px-2.5 py-1 text-xs hover:bg-accent">{q.label}</button>
                ))}
              </div>
              <Input type="datetime-local" value={form.next || ""} min={toLocalInput(new Date())} onChange={(e) => set("next", e.target.value)} data-testid="outcome-next-followup" />
            </div>
          ) : (
            <div className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">
              {kind === "won" ? "Won" : "Lost"} closes the lead — no follow-up needed. All pending follow-ups for both agents will be closed.
            </div>
          )}

          {channel === "call" && (
            <div className="space-y-1.5">
              <Label>Call duration (minutes)</Label>
              <Input type="number" min="0" step="0.5" value={form.duration} onChange={(e) => set("duration", e.target.value)} data-testid="outcome-duration" />
            </div>
          )}

          {problems.length > 0 && (
            <div className="flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs text-amber-800 dark:text-amber-200" data-testid="outcome-missing">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" /> Still needed: {problems.join(", ")}
            </div>
          )}

          <Button onClick={submit} disabled={saving} className="h-12 w-full rounded-full text-base" data-testid="outcome-submit">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save update
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
